import React from 'react';
import {
  ApplicationAction,
  ApplicationActionType,
  DataStatus,
  useWizardEffect,
} from 'src/features/common';
import { useApplication } from 'src/hooks/useApplication';
import { extractSerialNumber, getProduct } from 'src/features/device-activation/helpers/utils';
import { ConfirmModal, EnterSerialModal } from 'src/features/device-activation/components';
import { Environment } from 'src/Environment';
import { BarcodeScanner } from '@vfs-digital-channels/ns-react-components';
import { notify } from 'src/features/common/components/toast/toast';
import { CatalogueItem } from '@vfs-digital-channels/ns-utils';

interface DuplicateSerialNumberProps {
  visible: boolean;
  onClose: () => void;
}

export const DuplicateSerialNumberModal = ({ visible, onClose }: DuplicateSerialNumberProps) => {
  return (
    <ConfirmModal
      heading="Scanning failed"
      subtext="This serial number has already been added. "
      onClose={onClose}
      onCancel={onClose}
      cancelButtonLabel="Close"
      visible={visible}
    />
  );
};

export function ScanDevice() {
  const { application, dispatch, catalogue, deviceActivationTealiumService } = useApplication();

  const [showScannedFailed, setShowScannedFailed] = React.useState(false);
  const [showEnterBarcode, setShowEnterBarcode] = React.useState(
    Environment.current.disabledFeatures.daScanner,
  );
  const [showDuplicateSerialModal, setShowDuplicateSerialModal] = React.useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = React.useState<boolean>(true);

  const daScannerEnabled = React.useMemo(
    (): boolean => !Environment.current.disabledFeatures.daScanner,
    [],
  );

  const { setLoadingOverlayVisible } = useWizardEffect(
    () => ({
      nextButtonLabel: 'Next',
      backButtonVisible: true,
      nextButtonEnabled: false,
    }),
    [application],
  );

  const addSerialNumber = (serialNumber: string, products: CatalogueItem[]) => {
    const product = getProduct(products, serialNumber.toUpperCase());
    const isDuplicate = application.cardMachines?.find((cardMachine) =>
      cardMachine.barcode.toLowerCase().includes(serialNumber.toLowerCase()),
    );
    setShowDuplicateSerialModal(!!isDuplicate);

    const actions: ApplicationAction[] = isDuplicate
      ? []
      : [
          {
            type: ApplicationActionType.UPDATE_CARD_MACHINE,
            cardMachines: [
              ...(application.cardMachines || []),
              {
                barcode: serialNumber.toUpperCase(),
                name: '',
                productId: product.device.id,
                acquiringChannel: product.device.acquiringChannel,
              },
            ],
          },
          { type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP },
        ];

    dispatch({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions,
    });
  };

  return (
    <section className="fixed inset-0 z-50 flex flex-col p-4 bg-black bg-opacity-90 md:p-7">
      <EnterSerialModal
        showDuplicateModal={(val) => {
          setShowEnterBarcode(false);
          setShowDuplicateSerialModal(val);
        }}
        onVisible={() => {
          application.deviceActivationJourney &&
            deviceActivationTealiumService?.onEnterBarcodeStart(application.referenceNumber);
        }}
        onContinue={() => {
          application.deviceActivationJourney &&
            deviceActivationTealiumService?.onEnterBarcodeEnd(application.referenceNumber);
        }}
        onError={() => {
          application.deviceActivationJourney &&
            deviceActivationTealiumService?.onEnterBarcodeError(application.referenceNumber);
        }}
        visible={showEnterBarcode}
        onClose={() => {
          dispatch({ type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP });
        }}
      />
      <DuplicateSerialNumberModal
        visible={showDuplicateSerialModal}
        onClose={() => {
          setShowDuplicateSerialModal(false);
          dispatch({
            type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP,
          });
        }}
      />
      {daScannerEnabled && (
        <>
          <ConfirmModal
            heading="Scanning failed"
            subtext={'It looks like the scanning failed'}
            confirmButtonLabel="Retry"
            cancelButtonLabel="Enter manually"
            onCancel={() => {
              setShowScannedFailed(false);
              setShowEnterBarcode(true);
            }}
            onConfirm={() => {
              setShowScannedFailed(false);
              setShowBarcodeScanner(true);
            }}
            visible={showScannedFailed}
            onClose={() => setShowScannedFailed(false)}
          />
          <BarcodeScanner
            onScannedText={(text) => {
              setLoadingOverlayVisible(true);
              const serialNumber = extractSerialNumber(text || '');

              setLoadingOverlayVisible(false);

              if (!serialNumber) {
                setShowScannedFailed(true);
                return;
              }

              addSerialNumber(serialNumber, catalogue.products);

              notify({
                children: 'Card machine added successfully',
                autoClose: 5000,
              });

              setShowBarcodeScanner(false);
            }}
            open={showBarcodeScanner && catalogue.statusProducts === DataStatus.LOADED}
            onClose={() => {
              setShowBarcodeScanner(false);
              dispatch({ type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP });
            }}
            onEnterManually={() => {
              setShowBarcodeScanner(false);
              setShowEnterBarcode(true);
            }}
          />
        </>
      )}
    </section>
  );
}
